import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { featureRequests } from '../../api/client'
import PageHeader from '../../components/PageHeader'

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    // Try parsing "YYYY-MM-DD HH:MM:SS Timezone" format
    const match = dateString.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})/)
    if (match) {
      return new Date(match[1] + 'T' + match[2] + 'Z').toLocaleDateString()
    }
    return ''
  }
  return date.toLocaleDateString()
}

function StatusBadge({ status, statusDisplay }) {
  const colors = {
    new: 'bg-blue-100 text-blue-800',
    reviewing: 'bg-yellow-100 text-yellow-800',
    planned: 'bg-purple-100 text-purple-800',
    in_progress: 'bg-orange-100 text-orange-800',
    completed: 'bg-green-100 text-green-800',
    declined: 'bg-gray-100 text-gray-800',
  }
  return (
    <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {statusDisplay || status}
    </span>
  )
}

function TypeBadge({ type }) {
  return (
    <span className={`px-2 py-1 text-xs rounded-full ${type === 'bug' ? 'bg-red-100 text-red-800' : 'bg-indigo-100 text-indigo-800'}`}>
      {type === 'bug' ? 'Bug' : 'Feature'}
    </span>
  )
}

function RequestDetailModal({ request, onClose, onDelete }) {
  if (!request) return null

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${request.title}"? This cannot be undone.`)) return
    onDelete(request.id)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TypeBadge type={request.feedbackType} />
            <StatusBadge status={request.status} statusDisplay={request.statusDisplay} />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{request.title}</h2>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
              <span>{formatDate(request.createdAt)}</span>
              {request.submittedBy && <span>by {request.submittedBy}</span>}
            </div>
          </div>

          {request.category && (
            <div>
              <span className="inline-block px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                {request.category}
              </span>
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-1">Description</h3>
            <p className="text-gray-600 whitespace-pre-wrap">{request.description}</p>
          </div>

          {request.notes && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Notes</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{request.notes}</p>
            </div>
          )}

          {request.resolvedNote && (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Resolution</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{request.resolvedNote}</p>
            </div>
          )}

          {request.resolvedAt && (
            <div className="text-sm text-gray-500">
              Resolved on {formatDate(request.resolvedAt)}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-3">
          <button
            onClick={handleDelete}
            className="py-2 px-4 text-red-600 hover:bg-red-50 rounded-lg"
          >
            Delete
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

function NewRequestModal({ isOpen, onClose, onSubmit, categories }) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [feedbackType, setFeedbackType] = useState('feature')
  const [categoryId, setCategoryId] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      title,
      description,
      feedbackType,
      categoryId: categoryId ? parseInt(categoryId) : null,
    })
    // Reset form
    setTitle('')
    setDescription('')
    setFeedbackType('feature')
    setCategoryId('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Request</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFeedbackType('feature')}
                className={`flex-1 py-2 px-4 rounded-lg border ${feedbackType === 'feature' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 text-gray-600'}`}
              >
                Feature Request
              </button>
              <button
                type="button"
                onClick={() => setFeedbackType('bug')}
                className={`flex-1 py-2 px-4 rounded-lg border ${feedbackType === 'bug' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600'}`}
              >
                Bug Report
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of your request"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide details about the feature or describe the bug..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {categories && categories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category (optional)</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a category...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function FeatureRequestsPage() {
  const [showNewModal, setShowNewModal] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [filter, setFilter] = useState('open') // 'open', 'all', 'completed'
  const queryClient = useQueryClient()

  const { data: requests, isLoading } = useQuery({
    queryKey: ['featureRequests'],
    queryFn: featureRequests.list,
  })

  const { data: categories } = useQuery({
    queryKey: ['featureRequestCategories'],
    queryFn: featureRequests.getCategories,
  })

  const createMutation = useMutation({
    mutationFn: featureRequests.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureRequests'] })
      setShowNewModal(false)
    },
  })

  const handleDelete = async (id) => {
    try {
      await featureRequests.delete(id)
      queryClient.invalidateQueries({ queryKey: ['featureRequests'] })
      setSelectedRequest(null)
    } catch (err) {
      console.error('Failed to delete feature request:', err)
    }
  }

  const filteredRequests = requests?.filter(req => {
    if (filter === 'open') {
      return ['new', 'reviewing', 'planned', 'in_progress'].includes(req.status)
    }
    if (filter === 'completed') {
      return ['completed', 'declined'].includes(req.status)
    }
    return true
  }) || []

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        title="Feature Requests"
        showBack
        actions={
          <button
            onClick={() => setShowNewModal(true)}
            className="p-2 text-primary-600 hover:text-primary-700"
            title="New request"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        }
      />

      {/* Filter Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex">
          {[
            { key: 'open', label: 'Open' },
            { key: 'completed', label: 'Completed' },
            { key: 'all', label: 'All' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-1 py-3 text-sm font-medium ${filter === tab.key ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <p>No {filter === 'all' ? '' : filter} requests found</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="mt-4 text-primary-600 hover:text-primary-700"
            >
              Submit a new request
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map(request => (
              <div
                key={request.id}
                className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedRequest(request)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TypeBadge type={request.feedbackType} />
                      <StatusBadge status={request.status} statusDisplay={request.statusDisplay} />
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">{request.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{request.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>{formatDate(request.createdAt)}</span>
                      {request.category && <span>{request.category}</span>}
                      {request.submittedBy && <span>by {request.submittedBy}</span>}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <NewRequestModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSubmit={createMutation.mutate}
        categories={categories}
      />

      <RequestDetailModal
        request={selectedRequest}
        onClose={() => setSelectedRequest(null)}
        onDelete={handleDelete}
      />
    </div>
  )
}
